import { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { validateCoupon, getCartOffer } from '../api/real/couponsApi';
import { validateCart } from '../api/real/cartApi';
import { applyCartCheck } from '../utils/cartCheck';
import { useMarket } from './MarketContext';
import env from '../config/env';
import { trackAddToCart, trackRemoveFromCart } from '../utils/analytics';
import { fireAddToCartConversion } from '../utils/googleAds';
import { pendingCoupon, clearPendingCoupon } from '../utils/pendingCoupon';

const CartContext = createContext();

// The cart keeps the chosen size/title as text ("100ml", "Default"); find that variant's id for the server.
const variantIdOf = (item) => {
  const sv = item.selectedVariant;
  if (sv && typeof sv === 'object' && sv.id != null) return sv.id;
  const variants = item.variants || [];
  const match = typeof sv === 'string'
    ? variants.find((v) => v && (v.size === sv || v.title === sv || v.option1 === sv))
    : null;
  return (match ?? variants[0])?.id ?? null;
};

// Normalize selectedVariant to a consistent string key for comparison.
// Handles null, undefined, strings, and objects (e.g. { size: '100ml' } → '100ml').
const variantKey = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') return String(v.size || v.id || v.title || JSON.stringify(v));
  return String(v);
};

// Merge duplicate items (same product + same variant) that may exist in saved cart data.
const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];
  const map = new Map();
  for (const item of items) {
    const key = `${item.id}::${variantKey(item.selectedVariant)}`;
    if (map.has(key)) {
      const ex = map.get(key);
      map.set(key, { ...ex, quantity: ex.quantity + (item.quantity || 1) });
    } else {
      map.set(key, { ...item, quantity: item.quantity || 1 });
    }
  }
  return Array.from(map.values());
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(i =>
        i.id === action.payload.id &&
        variantKey(i.selectedVariant) === variantKey(action.payload.selectedVariant)
      );
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.id === action.payload.id &&
            variantKey(i.selectedVariant) === variantKey(action.payload.selectedVariant)
              ? { ...i, quantity: i.quantity + (action.payload.quantity || 1) }
              : i
          ),
        };
      }
      return { ...state, items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }] };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(i => !(
          i.id === action.payload.id &&
          variantKey(i.selectedVariant) === variantKey(action.payload.selectedVariant)
        )),
      };
    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(i => !(
            i.id === action.payload.id &&
            variantKey(i.selectedVariant) === variantKey(action.payload.selectedVariant)
          )),
        };
      }
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id &&
          variantKey(i.selectedVariant) === variantKey(action.payload.selectedVariant)
            ? { ...i, quantity: action.payload.quantity }
            : i
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'APPLY_CHECK':
      return { ...state, items: applyCartCheck(state.items, action.payload, variantIdOf).items };
    default:
      return state;
  }
};

const loadCart = () => {
  try {
    const saved = localStorage.getItem('skinnthrive_cart');
    if (!saved) return { items: [] };
    const parsed = JSON.parse(saved);
    // Merge any duplicate items that may have accumulated in localStorage.
    return { ...parsed, items: normalizeCartItems(parsed.items || []) };
  } catch { return { items: [] }; }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadCart);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState(null);   // { code, type, discountAmount, message }
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Debounce ref to avoid hammering the API on every keystroke / rapid cart updates
  const revalidateTimer = useRef(null);

  // Buy X Get Y offer the server applies to this cart by itself (or the one it is close to — then `valid` is false
  // and `message` says what to add). Recomputed whenever the cart changes; the order is always priced by the server.
  const [offer, setOffer] = useState(null);
  const offerTimer = useRef(null);
  const offerRequest = useRef(0);
  // Set by the checkout: an offer may be prepaid-only or limited per customer, so the price shown at checkout must
  // be judged with the chosen payment method and e-mail, exactly as the server will judge the order.
  const [offerContext, setOfferContext] = useState({ paymentMode: null, customerEmail: null });
  const { currentMarket } = useMarket();
  const marketCode = currentMarket?.isDefault ? null : (currentMarket?.code ?? null);

  useEffect(() => {
    localStorage.setItem('skinnthrive_cart', JSON.stringify(state));
  }, [state]);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const addItem = (product, selectedVariant, quantity = 1) => {
    // Use the market-specific price if one was resolved for the current market.
    // This ensures the cart stores USD (or whatever market currency) instead of INR
    // when shopping in a non-default market.
    const effectivePrice = product.marketPrice != null ? product.marketPrice : product.price;
    const payload = {
      ...product,
      price: effectivePrice,
      currencyCode: product.marketCurrencyCode ?? 'INR',
      currencySymbol: product.marketCurrencySymbol ?? null,
      selectedVariant,
      quantity,
    };
    dispatch({ type: 'ADD_ITEM', payload });
    setIsDrawerOpen(true);
    trackAddToCart({ ...product, price: effectivePrice }, quantity);
    fireAddToCartConversion((Number(effectivePrice) || 0) * quantity, product.marketCurrencyCode ?? 'INR');
  };

  const removeItem = (id, selectedVariant) => {
    const item = state.items.find(i => i.id === id && variantKey(i.selectedVariant) === variantKey(selectedVariant));
    if (item) trackRemoveFromCart(item, item.quantity);
    dispatch({ type: 'REMOVE_ITEM', payload: { id, selectedVariant } });
  };
  const updateQuantity = (id, selectedVariant, quantity) => dispatch({ type: 'UPDATE_QUANTITY', payload: { id, selectedVariant, quantity } });
  const clearCart = () => dispatch({ type: 'CLEAR_CART' });

  // The cart lives in this browser, so prices and stock may have changed since things were added. Check it with the
  // server whenever the cart or the checkout opens; on any failure the cart stays as it is (the order is priced by the
  // server anyway).
  const checking = useRef(false);
  const recheckCart = useCallback(async () => {
    if (env.isMock || state.items.length === 0 || checking.current) return;
    checking.current = true;
    try {
      const result = await validateCart({
        items: state.items.map((i) => ({
          productId: i.id, variantId: variantIdOf(i), handle: i.handle, quantity: i.quantity, price: Number(i.price),
        })),
        marketCode,
      });
      const issues = result?.issues || [];
      if (issues.length === 0) return;
      applyCartCheck(state.items, issues, variantIdOf).notices
        .forEach((n) => toast(n, { icon: 'ℹ️', duration: 6000 }));
      dispatch({ type: 'APPLY_CHECK', payload: issues });
    } catch {
      // keep the cart as it is
    } finally {
      checking.current = false;
    }
  }, [state.items, marketCode]);

  useEffect(() => {
    if (isDrawerOpen || isCheckoutOpen) recheckCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isCheckoutOpen]);

  // Re-validate applied coupon when cart items change
  useEffect(() => {
    if (!appliedCoupon) return;
    clearTimeout(revalidateTimer.current);
    revalidateTimer.current = setTimeout(() => {
      revalidateCoupon(appliedCoupon.code, appliedCoupon.marketCode);
    }, 600);
    return () => clearTimeout(revalidateTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.items]);

  // Build the rich items array the backend needs for brand/category/tag filtering
  const buildCouponItems = useCallback(() => {
    return state.items.map((item) => ({
      productId: item.id,
      variantId: variantIdOf(item),
      title: item.title || '',
      quantity: item.quantity,
      price: item.price,
      brandName: item.vendor || '',
      categoryType: item.type || item.productType || '',
      tags: Array.isArray(item.tags) ? item.tags : (item.tags ? String(item.tags).split(',').map(t => t.trim()) : []),
      collectionIds: item.collections || item.collectionIds || [],
    }));
  }, [state.items]);

  useEffect(() => {
    clearTimeout(offerTimer.current);
    if (env.isMock || state.items.length === 0) { setOffer(null); return undefined; }
    offerTimer.current = setTimeout(async () => {
      const ticket = ++offerRequest.current;
      try {
        const result = await getCartOffer({
          cartSubtotal: subtotal,
          cartItemCount: itemCount,
          items: buildCouponItems(),
          marketCode,
          paymentMode: offerContext.paymentMode,
          customerEmail: offerContext.customerEmail,
        });
        if (ticket !== offerRequest.current) return;   // a newer cart is already being priced
        setOffer(result ? {
          valid: !!result.valid,
          code: result.code,
          title: result.title || 'Offer',
          discount: result.valid ? Number(result.discountAmount || 0) : 0,
          message: result.message || '',
          // lineIndex follows the cart order we sent
          lines: (result.offerLines || []).map((l) => ({ ...l, discount: Number(l.discount || 0) })),
        } : null);
      } catch {
        // network trouble: keep what we had; checkout is priced by the server anyway
      }
    }, 400);
    return () => clearTimeout(offerTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.items, marketCode, offerContext.paymentMode, offerContext.customerEmail]);

  const offerDiscount = offer?.valid ? offer.discount : 0;
  /** The offer's share for one cart line: { discount, discountedQuantity } or null. */
  const offerForItem = useCallback((item) => {
    if (!offer?.valid) return null;
    const index = state.items.indexOf(item);
    const line = offer.lines.find((l) => l.lineIndex === index);
    return line && line.discountedQuantity > 0 ? line : null;
  }, [offer, state.items]);

  const revalidateCoupon = useCallback(async (code, marketCode) => {
    if (!code) return;
    try {
      const result = await validateCoupon(code, {
        cartSubtotal: subtotal,
        cartItemCount: itemCount,
        items: buildCouponItems(),
        marketCode: marketCode ?? null,
      });
      if (result.valid) {
        setAppliedCoupon({ code: result.code, type: result.type, discountAmount: Number(result.discountAmount), message: result.message, requiredPaymentMode: result.requiredPaymentMode || null });
        setCouponDiscount(Number(result.discountAmount));
        setCouponError('');
      } else {
        // Coupon no longer valid for updated cart — clear silently
        setAppliedCoupon(null);
        setCouponDiscount(0);
        setCouponError(result.message || 'Coupon is no longer applicable');
      }
    } catch {
      // Network error — keep current coupon applied, don't flash an error
    }
  }, [subtotal, itemCount, buildCouponItems]);

  const applyCoupon = useCallback(async (code, extraData = {}) => {
    if (!code?.trim()) {
      setCouponError('Please enter a coupon code');
      return false;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const result = await validateCoupon(code.trim().toUpperCase(), {
        cartSubtotal: subtotal,
        cartItemCount: itemCount,
        items: buildCouponItems(),
        ...extraData,
      });
      if (result.valid) {
        setAppliedCoupon({ code: result.code, type: result.type, discountAmount: Number(result.discountAmount), message: result.message, reasonCode: result.reasonCode, requiredPaymentMode: result.requiredPaymentMode || null, marketCode: extraData.marketCode ?? null });
        setCouponDiscount(Number(result.discountAmount));
        setCouponError('');
        return true;
      } else {
        setCouponError(result.message || 'Invalid coupon code');
        return false;
      }
    } catch (err) {
      setCouponError(err?.response?.data?.message || 'Failed to apply coupon. Please try again.');
      return false;
    } finally {
      setCouponLoading(false);
    }
  }, [subtotal, itemCount, buildCouponItems]);

  // A code that came with a link (the reorder e-mail's "Buy again"): applied once, as soon as the bag has something
  // in it and no other code is applied. Whatever the answer, it is not tried again (the bag shows why it didn't work).
  const pendingTried = useRef(null);
  useEffect(() => {
    if (appliedCoupon || state.items.length === 0) return;
    const code = pendingCoupon();
    if (!code || pendingTried.current === code) return;
    pendingTried.current = code;
    clearPendingCoupon();
    applyCoupon(code, { cartSubtotal: subtotal, marketCode }).then((ok) => {
      if (ok) toast.success(`Code ${code} applied`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.items.length, appliedCoupon, isDrawerOpen, isCheckoutOpen]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponError('');
  }, []);

  return (
    <CartContext.Provider value={{
      items: state.items,
      itemCount,
      subtotal,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      recheckCart,
      isDrawerOpen,
      setIsDrawerOpen,
      isCheckoutOpen,
      setIsCheckoutOpen,
      openCheckout: () => { setIsDrawerOpen(false); setIsCheckoutOpen(true); },
      // Coupon
      appliedCoupon,
      couponDiscount,
      offer,
      offerDiscount,
      offerForItem,
      setOfferContext,
      buildCouponItems,
      couponError,
      couponLoading,
      applyCoupon,
      removeCoupon,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};
